import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/providers/authContext';
import { Button, Modal, useToast } from '@/components/ui';

import { profilesForNavigationItem } from './navigation';

export interface SignOutDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

/**
 * "Sair da conta?" — asked in a small dialog of its own: "Sair" sits at the bottom of a list, one
 * stray tap there used to end the session, and a question squeezed into a menu row read as
 * cramped. The dialog also says what is kept, so the answer is easy. Shared by the account menu
 * of the top bar and the storefront's menu and drawer, so the three always sign out the same way.
 */
export function SignOutDialog({ open, onClose }: SignOutDialogProps) {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [signingOut, setSigningOut] = useState(false);
  // Only a buying profile has a cart to reassure about.
  const buys = profile !== null && profilesForNavigationItem('cart').includes(profile);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } catch {
      // The local session is already dropped by the provider, so the user is signed out here even
      // when revoking the refresh token failed; only the reassurance is missing.
      toast.show({
        tone: 'warning',
        title: 'Sessão encerrada neste dispositivo',
        description: 'Não foi possível avisar o servidor. Feche o navegador para garantir a saída.',
      });
    } finally {
      // Cached responses are tenant data; clearing them stops the next sign-in on the same
      // browser from painting the previous tenant's rows before its own request resolves.
      queryClient.clear();
      setSigningOut(false);
      onClose();
      await navigate('/login', { replace: true });
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!signingOut) {
          onClose();
        }
      }}
      title="Sair da conta?"
      description={
        buys
          ? 'Seu carrinho fica salvo. Você pode entrar de novo quando quiser.'
          : 'Você pode entrar de novo quando quiser.'
      }
      size="sm"
      footer={
        <>
          <Button variant="secondary" disabled={signingOut} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            loading={signingOut}
            loadingLabel="Saindo…"
            onClick={() => {
              void handleSignOut();
            }}
          >
            Sair
          </Button>
        </>
      }
    />
  );
}
